import type { ReactNode } from 'react';
import type {
  MapAssetGeometryKind,
  MapAssetStatItem,
  MapPreviewFrameStatus,
  MapStylingPreviewBackground,
  MapStylingPreviewCanvasProps,
  MapStylingPreviewCompareMode,
  MapStylingPreviewDensity,
  MapStylingPreviewFeatureState,
  MapStylingPreviewMatchState,
  MapStylingPreviewMode,
  MapStylingPreviewOption,
  MapStylingPreviewSampleSet,
} from '@workspace/silk-ui';

const PMTILES_PREFIX = 'pmtiles://';
const GEOJSON_PREFIX = 'geojson://';
const MAP_SOURCE_ARCHIVE_PATTERN = /^\/api\/v1\/map-sources\/([^/?#]+)\/archive(?:\/.*)?$/;
const MAP_SOURCE_GEOJSON_PATTERN = /^\/api\/v1\/map-sources\/([^/?#]+)\/geojson(?:\/.*)?$/;

export const STYLING_PREVIEW_MODE_OPTIONS: MapStylingPreviewOption<MapStylingPreviewMode>[] = [
  {
    value: 'focused_sample',
    label: 'Focused sample',
    description: 'Show the active layer in isolation with unrelated layers hidden.',
  },
  {
    value: 'pinned_features',
    label: 'Pinned features',
    description: 'Preview a small representative set of pinned examples.',
  },
  {
    value: 'context_preview',
    label: 'Context preview',
    description: 'Return to broader map context without losing the current draft.',
  },
];

export const STYLING_PREVIEW_SAMPLE_SET_OPTIONS: MapStylingPreviewOption<MapStylingPreviewSampleSet>[] = [
  {
    value: 'viewport_sample',
    label: 'Viewport sample',
    description: 'Use a small representative sample from the active map viewport.',
  },
  {
    value: 'pinned_examples',
    label: 'Pinned examples',
    description: 'Focus on host-selected sample features.',
  },
  {
    value: 'authored_test_set',
    label: 'Authored test set',
    description: 'Preview a reusable saved sample set when available.',
  },
];

export const STYLING_PREVIEW_BACKGROUND_OPTIONS: MapStylingPreviewOption<MapStylingPreviewBackground>[] = [
  {
    value: 'none',
    label: 'None',
    description: 'Render the style sample with no background context.',
  },
  {
    value: 'light_grid',
    label: 'Light grid',
    description: 'Use a light neutral grid to inspect icons, labels, and halos.',
  },
  {
    value: 'dark_grid',
    label: 'Dark grid',
    description: 'Use a dark neutral grid for contrast-sensitive symbols.',
  },
  {
    value: 'basemap',
    label: 'Basemap',
    description: 'Restore geographic context when needed.',
  },
];

export const STYLING_PREVIEW_DENSITY_OPTIONS: MapStylingPreviewOption<MapStylingPreviewDensity>[] = [
  {
    value: 'single_feature',
    label: 'Single feature',
    description: 'Inspect one representative symbol or geometry at a time.',
  },
  {
    value: 'small_cluster',
    label: 'Small cluster',
    description: 'Preview a compact overlapping group of features.',
  },
  {
    value: 'mixed_sample',
    label: 'Mixed sample',
    description: 'Preview a broader mix of representative matching states.',
  },
];

export const STYLING_PREVIEW_FEATURE_STATE_OPTIONS: MapStylingPreviewOption<MapStylingPreviewFeatureState>[] = [
  { value: 'normal', label: 'Normal', description: 'Default rendered state.' },
  { value: 'selected', label: 'Selected', description: 'Selected or active feature emphasis.' },
  { value: 'hovered', label: 'Hovered', description: 'Hover state for labels and symbols.' },
  { value: 'dimmed', label: 'Dimmed', description: 'Reduced emphasis state.' },
  { value: 'hidden_non_match', label: 'Hidden non-match', description: 'Hide features that do not match the active rule set.' },
];

export const STYLING_PREVIEW_COMPARE_OPTIONS: MapStylingPreviewOption<MapStylingPreviewCompareMode>[] = [
  { value: 'off', label: 'Compare off', description: 'Show only the current draft.' },
  {
    value: 'before_after_split',
    label: 'Before / after',
    description: 'Compare the baseline and current draft in a split preview.',
  },
  {
    value: 'side_by_side',
    label: 'Side by side',
    description: 'Compare the baseline and current draft in adjacent stages.',
  },
];

export type StylingPreviewCanvasTarget = {
  label?: string | null;
  sourceId: string;
  sourceLayer: string;
  geometryKind?: MapAssetGeometryKind | null;
  sampleSummary?: string | null;
};

export type StylingPreviewCanvasSampleFeature = {
  id: string | number;
  label: string;
  description?: string | null;
  badges?: string[];
  sourceId?: string | null;
  sourceLayer?: string | null;
  matchState?: MapStylingPreviewMatchState;
  selected?: boolean;
};

export interface StylingPreviewCanvasIsolationPlan {
  mode: MapStylingPreviewMode;
  sampleSet: MapStylingPreviewSampleSet;
  background: MapStylingPreviewBackground;
  density: MapStylingPreviewDensity;
  featureState: MapStylingPreviewFeatureState;
  compareMode: MapStylingPreviewCompareMode;
  targetSourceId: string;
  targetSourceLayer: string;
  targetSourceAliases: string[];
  focusLayerIds: string[];
  hiddenLayerIds: string[];
  sampleFeatureIds: Array<string | number>;
  pinnedFeatureIds: Array<string | number>;
  suppressBasemap: boolean;
  fitStrategy: 'target_bounds' | 'sample_bounds' | 'pinned_bounds' | 'current_view';
}

type StylingPreviewCanvasBuildArgs = {
  title?: string;
  description?: string | null;
  helperText?: string | null;
  status?: MapPreviewFrameStatus | null;
  loading?: boolean;
  errorMessage?: string | null;
  emptyMessage?: string | null;
  target?: StylingPreviewCanvasTarget | null;
  summaryItems?: MapAssetStatItem[];
  mode: MapStylingPreviewMode;
  onModeChange?: (next: MapStylingPreviewMode) => void;
  sampleSetValue?: MapStylingPreviewSampleSet;
  onSampleSetChange?: (next: MapStylingPreviewSampleSet) => void;
  backgroundValue?: MapStylingPreviewBackground;
  onBackgroundChange?: (next: MapStylingPreviewBackground) => void;
  densityValue?: MapStylingPreviewDensity;
  onDensityChange?: (next: MapStylingPreviewDensity) => void;
  featureStateValue?: MapStylingPreviewFeatureState;
  onFeatureStateChange?: (next: MapStylingPreviewFeatureState) => void;
  compareValue?: MapStylingPreviewCompareMode;
  onCompareChange?: (next: MapStylingPreviewCompareMode) => void;
  sampleFeatures?: StylingPreviewCanvasSampleFeature[];
  onSampleFeatureSelect?: (featureId: string) => void;
  toolbar?: ReactNode;
  viewport: ReactNode;
  footer?: ReactNode;
  className?: string;
};

type StylingPreviewIsolationArgs = {
  styleJson: Record<string, unknown>;
  target: StylingPreviewCanvasTarget;
  mode: MapStylingPreviewMode;
  sampleSet?: MapStylingPreviewSampleSet;
  background?: MapStylingPreviewBackground;
  density?: MapStylingPreviewDensity;
  featureState?: MapStylingPreviewFeatureState;
  compareMode?: MapStylingPreviewCompareMode;
  sampleFeatures?: StylingPreviewCanvasSampleFeature[];
};

export function buildStylingPreviewCanvasProps({
  title = 'Styling preview',
  description,
  helperText,
  status,
  loading,
  errorMessage,
  emptyMessage,
  target,
  summaryItems,
  mode,
  onModeChange,
  sampleSetValue = 'viewport_sample',
  onSampleSetChange,
  backgroundValue = 'none',
  onBackgroundChange,
  densityValue = 'mixed_sample',
  onDensityChange,
  featureStateValue = 'normal',
  onFeatureStateChange,
  compareValue = 'off',
  onCompareChange,
  sampleFeatures,
  onSampleFeatureSelect,
  toolbar,
  viewport,
  footer,
  className,
}: StylingPreviewCanvasBuildArgs): MapStylingPreviewCanvasProps {
  const resolvedTarget = target ?? {
    label: 'No layer selected',
    sourceId: '',
    sourceLayer: '',
    geometryKind: 'unknown',
    sampleSummary: null,
  };

  return {
    title,
    description:
      description ??
      'Isolate a small representative sample so advanced styling changes can be evaluated without full-map noise.',
    helperText: helperText ?? describeStylingPreviewMode(mode, sampleFeatures),
    status: status ?? buildStylingPreviewStatus(mode, sampleFeatures),
    loading,
    errorMessage,
    emptyMessage: emptyMessage ?? buildStylingPreviewEmptyMessage(mode, resolvedTarget, sampleFeatures),
    target: {
      label: resolvedTarget.label?.trim() || 'No layer selected',
      sourceId: resolvedTarget.sourceId,
      sourceLayer: resolvedTarget.sourceLayer,
      geometryKind: resolvedTarget.geometryKind ?? 'unknown',
      sampleSummary: resolvedTarget.sampleSummary ?? null,
    },
    summaryItems,
    mode,
    modeOptions: STYLING_PREVIEW_MODE_OPTIONS,
    onModeChange,
    sampleSetValue,
    sampleSetOptions: STYLING_PREVIEW_SAMPLE_SET_OPTIONS,
    onSampleSetChange,
    backgroundValue,
    backgroundOptions: STYLING_PREVIEW_BACKGROUND_OPTIONS,
    onBackgroundChange,
    densityValue,
    densityOptions: STYLING_PREVIEW_DENSITY_OPTIONS,
    onDensityChange,
    featureStateValue,
    featureStateOptions: STYLING_PREVIEW_FEATURE_STATE_OPTIONS,
    onFeatureStateChange,
    compareValue,
    compareOptions: STYLING_PREVIEW_COMPARE_OPTIONS,
    onCompareChange,
    sampleFeatures: sampleFeatures?.map(feature => ({
      id: String(feature.id),
      label: feature.label,
      description: feature.description ?? null,
      badges: feature.badges,
      sourceId: feature.sourceId ?? null,
      sourceLayer: feature.sourceLayer ?? null,
      matchState: feature.matchState,
      selected: feature.selected,
    })),
    onSampleFeatureSelect,
    toolbar,
    viewport,
    footer,
    className,
  };
}

export function buildStylingPreviewIsolationPlan({
  styleJson,
  target,
  mode,
  sampleSet = 'viewport_sample',
  background = 'none',
  density = 'mixed_sample',
  featureState = 'normal',
  compareMode = 'off',
  sampleFeatures = [],
}: StylingPreviewIsolationArgs): StylingPreviewCanvasIsolationPlan {
  const targetSourceAliases = resolveTargetSourceAliases(styleJson, target.sourceId);
  const focusLayerIds = resolveFocusLayerIds(styleJson, targetSourceAliases, target.sourceLayer);
  const allLayerIds = readStyleLayerIds(styleJson);
  const hiddenLayerIds =
    mode === 'context_preview' || focusLayerIds.length === 0
      ? []
      : allLayerIds.filter(layerId => !focusLayerIds.includes(layerId));
  const sampleFeatureIds = sampleFeatures.map(feature => feature.id);
  const selectedSampleIds = sampleFeatures.filter(feature => feature.selected).map(feature => feature.id);
  const pinnedFeatureIds =
    sampleSet === 'pinned_examples'
      ? (selectedSampleIds.length > 0 ? selectedSampleIds : sampleFeatureIds)
      : selectedSampleIds;

  return {
    mode,
    sampleSet,
    background,
    density,
    featureState,
    compareMode,
    targetSourceId: target.sourceId,
    targetSourceLayer: target.sourceLayer,
    targetSourceAliases,
    focusLayerIds,
    hiddenLayerIds,
    sampleFeatureIds,
    pinnedFeatureIds,
    suppressBasemap: background !== 'basemap',
    fitStrategy:
      mode === 'context_preview'
        ? 'current_view'
        : pinnedFeatureIds.length > 0
          ? 'pinned_bounds'
          : sampleFeatureIds.length > 0
            ? 'sample_bounds'
            : 'target_bounds',
  };
}

export function applyStylingPreviewIsolationPlan(
  styleJson: Record<string, unknown>,
  plan: StylingPreviewCanvasIsolationPlan,
): Record<string, unknown> {
  const clone = typeof structuredClone === 'function'
    ? structuredClone(styleJson)
    : JSON.parse(JSON.stringify(styleJson)) as Record<string, unknown>;
  const layers = Array.isArray(clone.layers) ? (clone.layers as Array<Record<string, unknown>>) : [];
  const shouldRestrictToPinnedFeatures = plan.mode === 'pinned_features' && plan.pinnedFeatureIds.length > 0;
  if (plan.hiddenLayerIds.length === 0 && !shouldRestrictToPinnedFeatures) {
    return clone;
  }

  const hiddenLayerIds = new Set(plan.hiddenLayerIds);
  const focusLayerIds = new Set(plan.focusLayerIds);
  const pinnedFeatureFilter = shouldRestrictToPinnedFeatures
    ? buildFeatureIdFilter(plan.pinnedFeatureIds)
    : null;
  clone.layers = layers.map(layer => {
    const layerId = typeof layer.id === 'string' ? layer.id : '';
    const nextLayer = {
      ...layer,
    };
    if (hiddenLayerIds.has(layerId)) {
      const layout =
        layer.layout && typeof layer.layout === 'object'
          ? { ...(layer.layout as Record<string, unknown>) }
          : {};
      layout.visibility = 'none';
      nextLayer.layout = layout;
    }
    if (pinnedFeatureFilter && focusLayerIds.has(layerId)) {
      nextLayer.filter = mergeLayerFilters(layer.filter, pinnedFeatureFilter);
    }
    return nextLayer;
  });

  return clone;
}

function buildFeatureIdFilter(featureIds: Array<string | number>) {
  const normalizedFeatureIds = featureIds
    .map(featureId => typeof featureId === 'string' ? featureId.trim() : featureId)
    .filter(featureId => !(typeof featureId === 'string' && featureId.length === 0));
  if (normalizedFeatureIds.length === 0) {
    return null;
  }
  return ['in', ['id'], ['literal', normalizedFeatureIds]];
}

function mergeLayerFilters(existingFilter: unknown, nextFilter: unknown) {
  if (!Array.isArray(nextFilter)) {
    return existingFilter;
  }
  if (!Array.isArray(existingFilter) || existingFilter.length === 0) {
    return nextFilter;
  }
  return ['all', existingFilter, nextFilter];
}

function buildStylingPreviewStatus(
  mode: MapStylingPreviewMode,
  sampleFeatures?: StylingPreviewCanvasSampleFeature[],
): MapPreviewFrameStatus {
  const sampleCount = sampleFeatures?.length ?? 0;
  if (mode === 'pinned_features') {
    return {
      label: sampleCount > 0 ? `${sampleCount} pinned` : 'Pin samples',
      tone: sampleCount > 0 ? 'default' : 'warning',
    };
  }
  if (mode === 'context_preview') {
    return {
      label: 'Context',
      tone: 'muted',
    };
  }
  return {
    label: sampleCount > 0 ? `${sampleCount} samples` : 'Isolated',
    tone: 'success',
  };
}

function buildStylingPreviewEmptyMessage(
  mode: MapStylingPreviewMode,
  target: StylingPreviewCanvasTarget,
  sampleFeatures?: StylingPreviewCanvasSampleFeature[],
): string {
  if (!target.sourceId || !target.sourceLayer) {
    return 'Choose a layer to isolate a styling preview sample.';
  }
  if (mode === 'pinned_features' && (sampleFeatures?.length ?? 0) === 0) {
    return 'Pin a few representative features to compare matching and non-matching states.';
  }
  return 'No preview sample is available for the current styling target yet.';
}

function describeStylingPreviewMode(
  mode: MapStylingPreviewMode,
  sampleFeatures?: StylingPreviewCanvasSampleFeature[],
): string {
  if (mode === 'pinned_features') {
    const count = sampleFeatures?.length ?? 0;
    return count > 0
      ? `Comparing ${count} pinned feature${count === 1 ? '' : 's'} in isolation.`
      : 'Pin representative features to isolate the style cases you care about.';
  }
  if (mode === 'context_preview') {
    return 'Broader geographic context is restored, but the current draft stays active.';
  }
  return 'Only the active layer sample stays visible so symbol, label, and rule changes are easier to inspect.';
}

function readStyleLayerIds(styleJson: Record<string, unknown>) {
  const layers = Array.isArray(styleJson.layers) ? (styleJson.layers as Array<Record<string, unknown>>) : [];
  return layers
    .map(layer => (typeof layer.id === 'string' ? layer.id : ''))
    .filter(Boolean);
}

function resolveFocusLayerIds(
  styleJson: Record<string, unknown>,
  targetSourceAliases: string[],
  targetSourceLayer: string,
) {
  const layers = Array.isArray(styleJson.layers) ? (styleJson.layers as Array<Record<string, unknown>>) : [];
  const aliasSet = new Set(targetSourceAliases);
  return layers
    .filter(layer => {
      const sourceAlias = typeof layer.source === 'string' ? layer.source : '';
      if (!aliasSet.has(sourceAlias)) {
        return false;
      }
      const layerSourceLayer = typeof layer['source-layer'] === 'string' ? layer['source-layer'] : '';
      if (layerSourceLayer) {
        return layerSourceLayer === targetSourceLayer;
      }
      return targetSourceLayer === 'features';
    })
    .map(layer => (typeof layer.id === 'string' ? layer.id : ''))
    .filter(Boolean);
}

function resolveTargetSourceAliases(styleJson: Record<string, unknown>, targetSourceId: string) {
  const styleSources =
    styleJson.sources && typeof styleJson.sources === 'object'
      ? (styleJson.sources as Record<string, Record<string, unknown>>)
      : {};

  return Object.entries(styleSources)
    .filter(([alias, source]) => alias === targetSourceId || resolveStyleSourceId(source) === targetSourceId)
    .map(([alias]) => alias);
}

function resolveStyleSourceId(styleSource?: Record<string, unknown> | null) {
  if (!styleSource || typeof styleSource !== 'object') {
    return '';
  }

  const rawUrl = typeof styleSource.url === 'string' ? styleSource.url.trim() : '';
  const sourceIdFromUrl = resolveSourceIdFromPrefixedRef(rawUrl, PMTILES_PREFIX, MAP_SOURCE_ARCHIVE_PATTERN);
  if (sourceIdFromUrl) {
    return sourceIdFromUrl;
  }

  const rawData = typeof styleSource.data === 'string' ? styleSource.data.trim() : '';
  const sourceIdFromData = resolveSourceIdFromPrefixedRef(rawData, GEOJSON_PREFIX, MAP_SOURCE_GEOJSON_PATTERN);
  if (sourceIdFromData) {
    return sourceIdFromData;
  }

  const runtimeGeoJsonMatch = extractMapRequestPathname(rawData).match(MAP_SOURCE_GEOJSON_PATTERN);
  return runtimeGeoJsonMatch?.[1] ? decodeURIComponent(runtimeGeoJsonMatch[1]) : '';
}

function resolveSourceIdFromPrefixedRef(value: string, prefix: string, runtimePattern: RegExp) {
  if (!value.startsWith(prefix)) {
    return '';
  }

  const candidate = value.slice(prefix.length).split('?', 1)[0].trim();
  if (!candidate || candidate.includes('://') || candidate.startsWith('/')) {
    const runtimeMatch = extractMapRequestPathname(candidate).match(runtimePattern);
    return runtimeMatch?.[1] ? decodeURIComponent(runtimeMatch[1]) : '';
  }

  return candidate;
}

function extractMapRequestPathname(value: string) {
  const candidate = value.trim();
  if (!candidate) {
    return '';
  }
  try {
    if (candidate.startsWith('http://') || candidate.startsWith('https://')) {
      return new URL(candidate).pathname;
    }
  } catch {
    return '';
  }
  return candidate.startsWith('/') ? candidate : '';
}
